import type { AttachmentUpload } from '@live-stream/shared'
import { putWithProgress } from '../meeting/attachments.js'
import { crashBuffer, type BufferedFile } from './crash-buffer.js'
import { createPartSlicer } from './parts.js'

/**
 * One file's journey from MediaRecorder to object storage: chunks in, equal
 * parts out, each part written to the crash buffer before it is sent and
 * removed once storage has it, parts sent one at a time in order (the
 * local driver appends; a bucket does not care), and a final `complete`
 * once the recorder has stopped and the last part is in.
 *
 * The addresses come from whoever owns the session — the socket while the
 * meeting runs, HTTP for a resume after it — through `addressFor`.
 */

export interface UploaderHooks {
  addressFor(partNumber: number): Promise<AttachmentUpload>
  complete(durationMs: number | null): Promise<void>
  onProgress?(state: UploaderProgress): void
}

export interface UploaderProgress {
  partsSent: number
  partsPending: number
  bytesSent: number
  failed: string | null
}

export class PartUploader {
  private readonly slicer
  private nextPart = 1
  private queue: Array<{ partNumber: number; blob: Blob }> = []
  private sending = false
  private stopped = false
  private durationMs: number | null = null
  private bytesSent = 0
  private partsSent = 0
  private failed: string | null = null
  private finished: Promise<void> | null = null
  private resolveFinished: (() => void) | null = null
  private rejectFinished: ((err: Error) => void) | null = null

  constructor(
    private readonly file: BufferedFile,
    partBytes: number,
    private readonly hooks: UploaderHooks,
  ) {
    this.slicer = createPartSlicer(partBytes)
    void crashBuffer.rememberFile(file)
  }

  /** A chunk from MediaRecorder. */
  push(chunk: Blob): void {
    if (this.stopped) return
    for (const part of this.slicer.push(chunk)) this.enqueue(part)
  }

  /** The recorder stopped: cut the last part and finish once everything is sent. */
  stop(durationMs: number | null): Promise<void> {
    if (this.finished) return this.finished
    this.stopped = true
    this.durationMs = durationMs
    const last = this.slicer.flush()
    if (last) this.enqueue(last)
    void crashBuffer.updateFile(this.file.fileId, { finished: true, durationMs })
    this.finished = new Promise<void>((resolve, reject) => {
      this.resolveFinished = resolve
      this.rejectFinished = reject
    })
    void this.drain()
    return this.finished
  }

  /** The take is thrown away: nothing more is sent, and the parts on the disk are let go. */
  async abandon(): Promise<void> {
    this.stopped = true
    this.queue = []
    this.failed = 'abandoned'
    await crashBuffer.forgetFile(this.file.fileId)
    this.rejectFinished?.(new Error('abandoned'))
  }

  /** Resume: parts already on the disk, in order, with the recorder long gone. */
  static async resume(file: BufferedFile, hooks: UploaderHooks): Promise<void> {
    const parts = await crashBuffer.partsOf(file.fileId)
    if (parts.length === 0) {
      await hooks.complete(file.durationMs).catch(() => undefined)
      await crashBuffer.forgetFile(file.fileId)
      return
    }
    const uploader = new PartUploader(file, Number.MAX_SAFE_INTEGER, hooks)
    uploader.stopped = true
    uploader.durationMs = file.durationMs
    uploader.queue = parts.map((p) => ({ partNumber: p.partNumber, blob: p.blob }))
    uploader.nextPart = Math.max(...parts.map((p) => p.partNumber)) + 1
    uploader.finished = new Promise<void>((resolve, reject) => {
      uploader.resolveFinished = resolve
      uploader.rejectFinished = reject
    })
    void uploader.drain()
    return uploader.finished
  }

  private enqueue(blob: Blob): void {
    const partNumber = this.nextPart++
    this.queue.push({ partNumber, blob })
    // On the disk first, then on the wire. A tab that dies between the two
    // still has the bytes.
    void crashBuffer
      .putPart({ fileId: this.file.fileId, partNumber, blob })
      .then(() => this.drain())
  }

  private report(): void {
    this.hooks.onProgress?.({
      partsSent: this.partsSent,
      partsPending: this.queue.length,
      bytesSent: this.bytesSent,
      failed: this.failed,
    })
  }

  private async drain(): Promise<void> {
    if (this.sending || this.failed) return
    this.sending = true
    try {
      while (this.queue.length > 0) {
        const { partNumber, blob } = this.queue[0] as { partNumber: number; blob: Blob }
        await this.send(partNumber, blob)
        this.queue.shift()
        this.partsSent++
        this.bytesSent += blob.size
        await crashBuffer.deletePart(this.file.fileId, partNumber)
        this.report()
      }
      if (this.stopped && this.finished) {
        await this.hooks.complete(this.durationMs)
        await crashBuffer.forgetFile(this.file.fileId)
        this.resolveFinished?.()
      }
    } catch (err) {
      this.failed = err instanceof Error ? err.message : String(err)
      this.report()
      // The parts stay on the disk; the next page load offers to finish.
      this.rejectFinished?.(err instanceof Error ? err : new Error(this.failed))
    } finally {
      this.sending = false
    }
  }

  /** Three tries per part with a pause between: a blip is not a lost recording. */
  private async send(partNumber: number, blob: Blob): Promise<void> {
    let lastError: unknown = null
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const address = await this.hooks.addressFor(partNumber)
        await putWithProgress({ url: address.url, headers: address.headers, body: blob })
        return
      } catch (err) {
        lastError = err
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)))
      }
    }
    throw lastError instanceof Error ? lastError : new Error(String(lastError))
  }
}
